import ListsManager from './ListsManager';

/**
 * TweetActionsManager - Handles all tweet-related actions
 * Similar to TweetCellDelegate in Swift
 */
class TweetActionsManager {
    static instance = null;

    static getInstance() {
        if (!TweetActionsManager.instance) {
            TweetActionsManager.instance = new TweetActionsManager();
        }
        return TweetActionsManager.instance;
    }

    /**
     * Handle tapping on author name to open their profile
     * @param {string} username - The username to open
     * @param {boolean} isFromTelegram - Whether the source is Telegram
     * @param {object} navigation - The navigation object for React Router
     */
    handleAuthorNamePress(username, isFromTelegram, navigation) {
        if (!username) return;
        
        const url = isFromTelegram 
            ? `https://t.me/${username}` 
            : `https://x.com/${username}`;
        
        // Open URL in a new browser tab
        window.open(url, '_blank');
    }

    /**
     * Handle tapping on reply button
     * @param {object} tweet - The tweet object
     * @param {object} navigation - The navigation object for React Router
     */
    handleReplyTapped(tweet, navigation) {
        if (!tweet || !tweet.dependencies) return;
        
        const dependencyTweets = tweet.dependencies
            .filter(dep => dep.includes("(replied_to)"))
            .map(dep => {
                const parsedDep = this.parseDependencyDetails(dep);
                if (!parsedDep) return null;
                
                return {
                    username: "",
                    url: "",
                    reason: "",
                    tweet_content: parsedDep.content,
                    list_name: tweet.list_name,
                    created_at: "",
                    tweet_id: "",
                    author_name: parsedDep.username,
                    author_profile_image_url: "",
                    verified_type: "",
                    tweet_images: [],
                    dependencies: [],
                    source: 'twitter'
                };
            })
            .filter(Boolean);
        
        // In React Router, we use state to pass complex objects
        navigation.navigate('/thread/' + tweet.tweet_id, {
            state: {
                dependencyTweets,
                originalTweet: tweet,
                isQuoting: false
            }
        });
    }

    /**
     * Handle tapping on quote button
     * @param {object} tweet - The tweet object
     * @param {object} navigation - The navigation object
     */
    handleQuoteTapped(tweet, navigation) {
        if (!tweet || !tweet.dependencies) return;
        
        const dependencyTweets = tweet.dependencies
            .filter(dep => dep.includes("(quoted)") || dep.includes("(part_of_conversation)"))
            .map(dep => {
                const parsedDep = this.parseDependencyDetails(dep);
                if (!parsedDep) return null;
                
                return {
                    username: "",
                    url: "",
                    reason: "",
                    tweet_content: parsedDep.content,
                    list_name: tweet.list_name,
                    created_at: "",
                    tweet_id: "",
                    author_name: "",
                    author_profile_image_url: "",
                    verified_type: "",
                    tweet_images: [],
                    dependencies: [],
                    source: 'twitter'
                };
            })
            .filter(Boolean);
        
        navigation.navigate('/thread/' + tweet.tweet_id, {
            state: {
                dependencyTweets,
                originalTweet: tweet,
                isQuoting: true
            }
        });
    }

    /**
     * Handle tapping on info button
     * @param {object} tweet - The tweet object
     * @param {object} navigation - The navigation object
     */
    handleInfoButtonTapped(tweet, navigation) {
        if (!tweet) return;
        
        navigation.navigate('/reason', { 
            state: {
                tweet, 
                isFromNotification: false 
            }
        });
    }

    /**
     * Open a web view with the given URL
     * @param {string} url - The URL to open
     * @param {object} navigation - The navigation object
     */
    openWebView(url, navigation) {
        if (!url) return;
        
        navigation.navigate('/web-view', { 
            state: { url } 
        });
    }

    /**
     * Handle tapping on open button
     * @param {string} url - The URL to open
     * @param {object} navigation - The navigation object
     */
    handleOpenButtonTapped(url, navigation) {
        if (!url) return;
        
        // Open URL in a new browser tab instead of using the web view
        window.open(url, '_blank');
    }

    /**
     * Handle tapping on share button
     * @param {string} url - The URL to share
     */
    async handleShareButtonTapped(url) {
        if (!url) return;
        
        try {
            if (navigator.share) {
                await navigator.share({
                    url: url
                });
            } else {
                // Fallback for browsers that don't support Web Share API
                navigator.clipboard.writeText(url);
                alert('URL copied to clipboard!');
            }
        } catch (error) {
            console.error("Error sharing:", error);
            alert('Failed to share the content');
        }
    }

    /**
     * Handle tapping on translate button
     * @param {object} tweet - The tweet object
     * @param {object} navigation - The navigation object
     */
    handleTranslateButtonTapped(tweet, navigation) {
        if (!tweet || !tweet.tweet_content) {
            console.log("No tweet content available for translation.");
            return;
        }

        const targetLanguage = 'en'; // Default to English, could be from user settings
        
        // Fetch the translation
        ListsManager.translateTweet(tweet.tweet_content, targetLanguage)
            .then(translatedText => {
                if (translatedText) {
                    alert('Translation: ' + translatedText);
                } else {
                    alert('Translation Failed: Unable to translate the content');
                }
            })
            .catch(error => {
                console.error("Translation error:", error);
                alert('Translation Error: ' + (error.message || 'An error occurred during translation'));
            });
    }

    /**
     * Parse dependency details from a dependency string
     * @param {string} dependency - The dependency string to parse
     * @returns {object|null} The parsed dependency or null if parsing failed
     */
    parseDependencyDetails(dependency) {
        try {
            // Format: username: content (type)
            const matches = dependency.match(/(.+?):\s+(.+)\s+\((.+?)\)$/);
            if (!matches || matches.length < 4) return null;
            
            return {
                username: matches[1].trim(),
                content: matches[2].trim(),
                type: matches[3].trim()
            };
        } catch (error) {
            console.error("Error parsing dependency:", error);
            return null;
        }
    }
}

// Singleton instance
const tweetActionsManager = TweetActionsManager.getInstance();
export default tweetActionsManager; 